import { AlertTriangle, Coins } from 'lucide-react';
import GraficoLinha from '@/components/GraficoLinha';
import { useIndicesModal } from '@/stores/indicesModalStore';
import { Cartao, CartaoIndicador, ErroDoBloco, Esqueleto, EsqueletoIndicador, type Variacao } from './componentes';
import { reaisInteiros, variacaoComSinal } from './formatos';
import type { PainelMetais } from './consultas';
import { cn } from '@/lib/utils';

const dolar = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL', minimumFractionDigits: 4, maximumFractionDigits: 4 });

/** "2026-09-16" vira "16/09". */
function diaMes(iso: string): string {
  const [, mes, dia] = iso.split('-');
  return `${dia}/${mes}`;
}

/** Para quem compra, preço caindo é boa notícia: queda em verde, alta em vermelho. */
function paraQuemCompra(fracao: number | null | undefined): Variacao | null {
  if (fracao === null || fracao === undefined) return null;

  return {
    texto: variacaoComSinal(fracao),
    direcao: fracao > 0 ? 'sobe' : fracao < 0 ? 'desce' : 'estavel',
    leitura: fracao < 0 ? 'boa' : fracao > 0 ? 'ruim' : 'neutra',
  };
}

export function IndicadorMetais({ dados, carregando }: { dados?: PainelMetais; carregando: boolean }) {
  const abrirIndices = useIndicesModal((estado) => estado.abrir);

  if (carregando || !dados) return <EsqueletoIndicador />;

  const cobre = dados.hoje?.cobre_brl ?? null;

  return (
    <CartaoIndicador
      icone={Coins}
      rotulo="Cobre LME"
      valor={cobre !== null ? `${reaisInteiros.format(cobre)}/t` : '—'}
      variacao={paraQuemCompra(dados.variacoes?.semana?.cobre_brl)}
      detalhe={dados.ultimo_pregao ? `na semana · pregão de ${diaMes(dados.ultimo_pregao)}` : 'Sem pregão capturado'}
      onClick={abrirIndices}
    />
  );
}

/**
 * O painel do cobre à mão, como o Marcelo pediu: a tonelada em reais, o
 * alumínio e o dólar do dia, e a linha dos últimos pregões. O histórico
 * completo abre no mesmo modal da cotação.
 */
export function CartaoMetais({ dados, carregando, erro }: { dados?: PainelMetais; carregando: boolean; erro: boolean }) {
  const abrirIndices = useIndicesModal((estado) => estado.abrir);

  return (
    <Cartao
      titulo="Metais e dólar"
      subtitulo={dados?.ultimo_pregao ? `LME à vista e PTAX · pregão de ${diaMes(dados.ultimo_pregao)}` : 'LME à vista e dólar PTAX'}
      atalho={{ rotulo: 'Histórico', onClick: abrirIndices }}
    >
      {erro ? (
        <ErroDoBloco oQue="os índices" />
      ) : carregando || !dados ? (
        <div className="space-y-sm">
          <Esqueleto className="h-[40px] w-full" />
          <Esqueleto className="h-[40px] w-full" />
          <Esqueleto className="h-[40px] w-full" />
          <Esqueleto className="h-[140px] w-full" />
        </div>
      ) : !dados.hoje ? (
        <p className="text-[13px] text-muted">Nenhum pregão capturado ainda.</p>
      ) : (
        <>
          {dados.desatualizado && (
            <p className="mb-sm flex items-center gap-sm rounded-md border border-warning/40 bg-warning/5 px-sm py-xs text-[12px] text-ink">
              <AlertTriangle size={14} className="shrink-0 text-warning" />
              Último pregão com mais de quatro dias: a captura pode ter parado.
            </p>
          )}

          <table className="w-full text-[13px]">
            <thead>
              <tr className="text-[11px] text-muted">
                <th className="pb-xs text-left font-normal">Hoje</th>
                <th className="pb-xs text-right font-normal">Semana</th>
                <th className="pb-xs text-right font-normal">Mês</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              <Linha
                nome="Cobre"
                valor={dados.hoje.cobre_brl !== null ? `${reaisInteiros.format(dados.hoje.cobre_brl)}/t` : '—'}
                semana={dados.variacoes?.semana?.cobre_brl}
                mes={dados.variacoes?.mes?.cobre_brl}
              />
              <Linha
                nome="Alumínio"
                valor={dados.hoje.aluminio_brl !== null ? `${reaisInteiros.format(dados.hoje.aluminio_brl)}/t` : '—'}
                semana={dados.variacoes?.semana?.aluminio_brl}
                mes={dados.variacoes?.mes?.aluminio_brl}
              />
              <Linha
                nome="Dólar PTAX"
                valor={dados.hoje.dolar !== null ? dolar.format(dados.hoje.dolar) : '—'}
                semana={dados.variacoes?.semana?.dolar}
                mes={dados.variacoes?.mes?.dolar}
              />
            </tbody>
          </table>

          <p className="mt-md mb-xs text-[12px] text-muted">Cobre em R$/t, pregão a pregão</p>
          <GraficoLinha
            pontos={dados.periodos.map((p) => ({ rotulo: p.rotulo, rotuloCurto: diaMes(p.inicio), valor: p.cobre_brl }))}
            formatar={(v) => reaisInteiros.format(v)}
            altura={150}
            larguraMinima={240}
            descricao="Cobre na LME em reais por tonelada nos últimos pregões"
          />
        </>
      )}
    </Cartao>
  );
}

function Linha({ nome, valor, semana, mes }: { nome: string; valor: string; semana?: number | null; mes?: number | null }) {
  return (
    <tr>
      <td className="py-xs">
        <span className="block text-[12px] text-muted">{nome}</span>
        <span className="font-bold whitespace-nowrap text-ink tabular-nums">{valor}</span>
      </td>
      <Celula fracao={semana} />
      <Celula fracao={mes} />
    </tr>
  );
}

function Celula({ fracao }: { fracao?: number | null }) {
  const variacao = paraQuemCompra(fracao);

  return (
    <td
      className={cn(
        'py-xs text-right font-bold tabular-nums',
        variacao?.leitura === 'boa' ? 'text-success' : variacao?.leitura === 'ruim' ? 'text-danger' : 'text-muted',
      )}
    >
      {variacao?.texto ?? '—'}
    </td>
  );
}
